import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Phone, PhoneOff, PhoneMissed, PhoneIncoming, PhoneOutgoing,
  Video, VideoOff, Mic, MicOff, Search, Clock, Loader2, Bot, Languages
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import AIPalChat from "@/components/AIPalChat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RTC_CONFIG = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
    { urls: "stun:stun2.l.google.com:19302" },
    {
      urls: "turn:openrelay.metered.ca:80",
      username: "openrelayproject",
      credential: "openrelayproject"
    },
    {
      urls: "turn:openrelay.metered.ca:443",
      username: "openrelayproject",
      credential: "openrelayproject"
    }
  ]
};

export default function Calls() {
  const [callHistory, setCallHistory] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [activeTab, setActiveTab] = useState("history");

  const [incomingCall, setIncomingCall] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [callStatus, setCallStatus] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [connectionState, setConnectionState] = useState("");
  const [remoteStream, setRemoteStream] = useState(null);
  const [showCallpal, setShowCallpal] = useState(false);
  const [tolokyEnabled, setTolokyEnabled] = useState(false);
  const [tolokyToggling, setTolokyToggling] = useState(false);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const localStreamRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const callTimerRef = useRef(null);
  const callIdRef = useRef(null);
  const pollingRef = useRef(null);
  const statePollRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const activeCallRef = useRef(null);
  const incomingCallRef = useRef(null);
  const audioCtxRef = useRef(null);
  const audioSourceRef = useRef(null);

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const getCurrentUserId = () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        return JSON.parse(atob(token.split('.')[1])).user_id;
      } catch (e) {}
    }
    return null;
  };

  // Resume or create AudioContext on user gesture so autoplay is allowed
  const ensureAudioContext = useCallback(async () => {
    if (!audioCtxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      audioCtxRef.current = new AC();
    }
    if (audioCtxRef.current.state === 'suspended') {
      try { await audioCtxRef.current.resume(); } catch (e) { console.error("AudioContext resume error:", e); }
    }
  }, []);

  // Pipe a MediaStream into the AudioContext for guaranteed playback
  const connectStreamToAudioContext = useCallback(async (stream) => {
    if (!stream) return;
    // Ensure AudioContext exists and is running
    if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      audioCtxRef.current = new AC();
    }
    if (audioCtxRef.current.state === 'suspended') {
      try { await audioCtxRef.current.resume(); } catch (e) { console.error("AudioContext resume error:", e); }
    }
    // Disconnect previous source
    if (audioSourceRef.current) {
      try { audioSourceRef.current.disconnect(); } catch (e) {}
      audioSourceRef.current = null;
    }
    try {
      const source = audioCtxRef.current.createMediaStreamSource(stream);
      source.connect(audioCtxRef.current.destination);
      audioSourceRef.current = source;
      console.log("Connected remote audio to AudioContext, state:", audioCtxRef.current.state);
    } catch (e) {
      console.error("AudioContext connection error:", e);
    }
  }, []);

  const cleanupCall = useCallback(() => {
    console.log("Cleaning up call...");
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    if (statePollRef.current) { clearInterval(statePollRef.current); statePollRef.current = null; }
    if (callTimerRef.current) { clearInterval(callTimerRef.current); callTimerRef.current = null; }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => { track.stop(); console.log("Stopped local track:", track.kind); });
      localStreamRef.current = null;
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (audioSourceRef.current) {
      try { audioSourceRef.current.disconnect(); } catch (e) {}
      audioSourceRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (e) {}
      audioCtxRef.current = null;
    }
    remoteStreamRef.current = null;
    setRemoteStream(null);
    callIdRef.current = null;
  }, []);

  // Route remote stream to all output paths
  const attachRemoteStream = useCallback((stream) => {
    remoteStreamRef.current = stream;
    setRemoteStream(stream);

    // Video element (handles both audio and video tracks when visible)
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = stream;
      remoteVideoRef.current.play().catch(e => console.log("Video play error:", e));
    }

    // Dedicated audio element (tiny but non-zero size so browser decodes audio)
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = stream;
      remoteAudioRef.current.play().catch(e => console.log("Audio element play error:", e));
    }

    // Web Audio API path — guaranteed playback regardless of autoplay policy
    connectStreamToAudioContext(stream);
  }, [connectStreamToAudioContext]);

  // Update remote elements when remoteStream state changes
  useEffect(() => {
    if (remoteStream) {
      attachRemoteStream(remoteStream);
    }
  }, [remoteStream, attachRemoteStream]);

  // Keep refs in sync with state for polling closure
  useEffect(() => { activeCallRef.current = activeCall; }, [activeCall]);
  useEffect(() => { incomingCallRef.current = incomingCall; }, [incomingCall]);

  useEffect(() => {
    fetchCallHistory();
    fetchContacts();
    const checkIncoming = setInterval(() => {
      if (activeCallRef.current || incomingCallRef.current) return;
      checkIncomingCalls();
    }, 1500);
    return () => {
      clearInterval(checkIncoming);
      cleanupCall();
    };
  }, [cleanupCall]);

  const fetchCallHistory = async () => {
    try {
      const res = await axios.get(`${API}/comms/calls/history`, { headers: getHeaders(), withCredentials: true });
      setCallHistory(res.data || []);
    } catch (err) {}
  };

  const fetchContacts = async () => {
    try {
      const res = await axios.get(`${API}/comms/chat/users/search?q=`, { headers: getHeaders(), withCredentials: true });
      setContacts(res.data || []);
    } catch (err) {}
  };

  const checkIncomingCalls = async () => {
    if (activeCall || incomingCall) return;
    try {
      const res = await axios.get(`${API}/comms/calls/incoming`, { headers: getHeaders(), withCredentials: true });
      if (res.data?.id) {
        console.log("Incoming call detected:", res.data);
        setIncomingCall(res.data);
      }
    } catch (err) {}
  };

  const searchUsers = async (query) => {
    if (!query.trim()) { setSearchResults([]); return; }
    try {
      const res = await axios.get(`${API}/comms/chat/users/search?q=${encodeURIComponent(query)}`, { headers: getHeaders(), withCredentials: true });
      setSearchResults((res.data || []).filter(u => u.user_id !== getCurrentUserId()));
    } catch (err) {}
  };

  const startPollingIceCandidates = (pc, callId, timeoutMs = 15000) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      try {
        const icRes = await axios.get(`${API}/comms/calls/${callId}/ice-candidates`, { headers: getHeaders(), withCredentials: true });
        for (const candidate of (icRes.data?.candidates || [])) {
          try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); } catch (e) { /* already added */ }
        }
      } catch (err) {}
    }, 1000);
    setTimeout(() => {
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    }, timeoutMs);
  };

  const createPeerConnection = () => {
    console.log("Creating peer connection...");
    const pc = new RTCPeerConnection(RTC_CONFIG);

    pc.onicecandidate = async (event) => {
      if (event.candidate && callIdRef.current) {
        try {
          await axios.post(`${API}/comms/calls/${callIdRef.current}/ice-candidate`,
            { candidate: event.candidate.toJSON() },
            { headers: { ...getHeaders(), "Content-Type": "application/json" }, withCredentials: true }
          );
        } catch (err) { /* ICE candidate send failed — will be retried */ }
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log("ICE connection state:", pc.iceConnectionState);
      setConnectionState(pc.iceConnectionState);
      if (pc.iceConnectionState === "connected" || pc.iceConnectionState === "completed") {
        setCallStatus("connected");
        startCallTimer();
        toast.success("Call connected!");
      } else if (pc.iceConnectionState === "failed") {
        // Try ICE restart once before giving up
        if (!pc._iceRestartAttempted) {
          pc._iceRestartAttempted = true;
          console.log("ICE failed, attempting restart...");
          toast.info("Connection lost, reconnecting...");
          pc.restartIce();
        } else {
          toast.error("Connection failed - try again");
          handleEndCall();
        }
      } else if (pc.iceConnectionState === "disconnected") {
        // Only show warning if we were previously connected
        setConnectionState("disconnected");
      }
    };

    pc.ontrack = (event) => {
      console.log("=== ONTRACK EVENT ===", event.track.kind);
      const stream = event.streams[0];
      if (stream) {
        console.log("Remote stream ID:", stream.id, "tracks:", stream.getTracks().map(t => t.kind));
        attachRemoteStream(stream);
      } else {
        // Fallback: build stream from individual track
        if (!remoteStreamRef.current) {
          remoteStreamRef.current = new MediaStream();
        }
        const existingTrack = remoteStreamRef.current.getTracks().find(t => t.kind === event.track.kind);
        if (existingTrack) remoteStreamRef.current.removeTrack(existingTrack);
        remoteStreamRef.current.addTrack(event.track);
        const newStream = new MediaStream(remoteStreamRef.current.getTracks());
        attachRemoteStream(newStream);
      }

      // Log when remote track unmutes (audio data starts flowing)
      event.track.onunmute = () => console.log(`Remote ${event.track.kind} unmuted — data flowing`);
      event.track.onmute = () => console.log(`Remote ${event.track.kind} muted`);
      event.track.onended = () => console.log(`Remote ${event.track.kind} ended`);
    };

    pc.onconnectionstatechange = () => {
      console.log("Connection state:", pc.connectionState);
      if (pc.connectionState === "failed") {
        console.error("Connection failed completely — DTLS or transport error");
        // If ICE restart didn't help or wasn't triggered yet
        if (!pc._iceRestartAttempted) {
          pc._iceRestartAttempted = true;
          console.log("Attempting ICE restart from connectionstatechange...");
          pc.restartIce();
        }
      } else if (pc.connectionState === "connected") {
        console.log("Connection fully established (ICE + DTLS)");
      }
    };

    peerConnectionRef.current = pc;
    return pc;
  };

  const getMedia = async (withVideo) => {
    try {
      console.log("Getting media, video:", withVideo);
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: withVideo ? { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } } : false
      });
      console.log("Got local stream with tracks:", stream.getTracks().map(t => `${t.kind}:${t.enabled}`));
      localStreamRef.current = stream;

      if (localVideoRef.current && withVideo) {
        localVideoRef.current.srcObject = stream;
      }

      return stream;
    } catch (err) {
      console.error("getUserMedia error:", err);
      toast.error(`Camera/microphone access denied: ${err.message}`);
      return null;
    }
  };

  const initiateCall = async (user, isVideo = false) => {
    console.log(`=== INITIATING ${isVideo ? "VIDEO" : "AUDIO"} CALL to ${user.name} ===`);

    try {
      // Resume AudioContext on user gesture (the Call button click)
      ensureAudioContext();

      const stream = await getMedia(isVideo);
      if (!stream) return;

      const res = await axios.post(`${API}/comms/calls/initiate`,
        { recipient_id: user.user_id, call_type: isVideo ? "video" : "audio" },
        { headers: { ...getHeaders(), "Content-Type": "application/json" }, withCredentials: true }
      );

      const callData = res.data;
      callIdRef.current = callData.id;
      console.log("Call created:", callData.id);

      setActiveCall({ id: callData.id, participant: user, type: "outgoing", isVideo });
      setCallStatus("ringing");
      setIsVideoOn(isVideo);

      // Create peer connection with sendrecv audio transceiver
      const pc = createPeerConnection();

      // Add local tracks
      stream.getTracks().forEach(track => {
        console.log(`Adding local ${track.kind} track to PC`);
        pc.addTrack(track, stream);
      });

      // Create and send offer
      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: isVideo
      });
      console.log("Created offer");
      await pc.setLocalDescription(offer);

      await axios.post(`${API}/comms/calls/${callData.id}/offer`,
        { offer: { type: offer.type, sdp: offer.sdp } },
        { headers: { ...getHeaders(), "Content-Type": "application/json" }, withCredentials: true }
      );
      console.log("Offer sent to server");

      toast.info(`Calling ${user.name}...`);

      // Poll for answer (faster interval)
      pollingRef.current = setInterval(async () => {
        try {
          const answerRes = await axios.get(`${API}/comms/calls/${callData.id}/answer`, { headers: getHeaders(), withCredentials: true });

          if (answerRes.data?.answer) {
            console.log("=== RECEIVED ANSWER ===");
            clearInterval(pollingRef.current);
            pollingRef.current = null;

            try {
              await pc.setRemoteDescription(new RTCSessionDescription(answerRes.data.answer));
              console.log("Remote description set");
            } catch (sdpErr) {
              console.error("Failed to set remote description from answer:", sdpErr);
              toast.error("Connection setup failed");
              handleEndCall();
              return;
            }

            // Get and add any existing remote ICE candidates
            const icRes = await axios.get(`${API}/comms/calls/${callData.id}/ice-candidates`, { headers: getHeaders(), withCredentials: true });
            console.log("Got", icRes.data?.candidates?.length || 0, "remote ICE candidates");
            for (const candidate of (icRes.data?.candidates || [])) {
              try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); } catch (e) {}
            }

            // Start polling for more ICE candidates
            startPollingIceCandidates(pc, callData.id);
          }
        } catch (err) {
          // Check if call was ended (declined/missed)
          try {
            const stateRes = await axios.get(`${API}/comms/calls/${callData.id}/state`, { headers: getHeaders(), withCredentials: true });
            if (stateRes.data?.status === "ended") {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
              const reason = stateRes.data.end_reason;
              if (reason === "declined") toast.error("Call declined");
              else if (reason === "missed") toast.info("No answer");
              handleEndCall();
            }
          } catch (e) {}
        }
      }, 1000);

      // Timeout after 45s
      setTimeout(() => {
        if (callStatus === "ringing" && pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          toast.info("No answer");
          handleEndCall();
        }
      }, 45000);

    } catch (err) {
      console.error("initiateCall error:", err);
      toast.error("Failed to initiate call");
      cleanupCall();
      setActiveCall(null);
      setCallStatus(null);
    }
  };

  const answerCall = async () => {
    if (!incomingCall) return;

    const isVideo = incomingCall.call_type === "video";
    console.log(`=== ANSWERING ${isVideo ? "VIDEO" : "AUDIO"} CALL ===`);

    try {
      // Resume AudioContext on user gesture (the Answer button click)
      ensureAudioContext();

      const stream = await getMedia(isVideo);
      if (!stream) { declineCall(); return; }

      callIdRef.current = incomingCall.id;

      // Get offer from caller
      const offerRes = await axios.get(`${API}/comms/calls/${incomingCall.id}/offer`, { headers: getHeaders(), withCredentials: true });

      if (!offerRes.data?.offer) {
        toast.error("Call no longer available");
        setIncomingCall(null);
        cleanupCall();
        return;
      }

      console.log("Got offer from caller");

      // Create peer connection
      const pc = createPeerConnection();

      // Set remote description FIRST (creates transceivers matching the offer)
      await pc.setRemoteDescription(new RTCSessionDescription(offerRes.data.offer));
      console.log("Set remote description (offer)");

      // Then add local tracks — associates them with the transceivers created by setRemoteDescription
      stream.getTracks().forEach(track => {
        console.log(`Adding local ${track.kind} track to PC`);
        pc.addTrack(track, stream);
      });
      console.log("Local tracks added to transceivers");

      // Add any caller ICE candidates bundled with the offer
      if (offerRes.data.call?.caller_ice_candidates?.length) {
        console.log("Adding", offerRes.data.call.caller_ice_candidates.length, "initial caller ICE candidates");
        for (const candidate of offerRes.data.call.caller_ice_candidates) {
          try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); } catch (e) {}
        }
      }

      // Create answer
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      console.log("Created and set answer, SDP length:", answer.sdp?.length);

      // Send the SDP answer to the server
      await axios.post(`${API}/comms/calls/${incomingCall.id}/answer`,
        { answer: { type: answer.type, sdp: answer.sdp } },
        { headers: { ...getHeaders(), "Content-Type": "application/json" }, withCredentials: true }
      );
      console.log("Answer sent to server");

      setActiveCall({ id: incomingCall.id, participant: incomingCall.caller || incomingCall.participant, type: "incoming", isVideo });
      setCallStatus("connecting");
      setIsVideoOn(isVideo);
      setIncomingCall(null);

      // Poll for caller's ICE candidates
      startPollingIceCandidates(pc, incomingCall.id);

      // Poll call state to detect if caller ends the call
      if (statePollRef.current) clearInterval(statePollRef.current);
      statePollRef.current = setInterval(async () => {
        try {
          const stRes = await axios.get(`${API}/comms/calls/${incomingCall.id}/state`, { headers: getHeaders(), withCredentials: true });
          if (stRes.data?.status === "ended") {
            console.log("Call ended by remote peer");
            clearInterval(statePollRef.current);
            statePollRef.current = null;
            handleEndCall();
          }
        } catch (e) {}
      }, 2000);

    } catch (err) {
      console.error("answerCall error:", err);
      toast.error("Failed to answer call");
      cleanupCall();
      setIncomingCall(null);
    }
  };

  const declineCall = async () => {
    if (!incomingCall) return;
    try {
      await axios.post(`${API}/comms/calls/decline`, { call_id: incomingCall.id }, { headers: { ...getHeaders(), "Content-Type": "application/json" }, withCredentials: true });
      toast.info("Call declined");
    } catch (err) {}
    setIncomingCall(null);
  };

  const handleEndCall = async () => {
    const currentCallId = callIdRef.current;
    cleanupCall();
    setActiveCall(null);
    setCallStatus(null);
    setCallDuration(0);
    setIsMuted(false);
    setIsVideoOn(false);
    setConnectionState("");
    setTolokyEnabled(false);

    if (currentCallId) {
      try {
        await axios.post(`${API}/comms/calls/end`, { call_id: currentCallId }, { headers: { ...getHeaders(), "Content-Type": "application/json" }, withCredentials: true });
      } catch (err) {}
    }
    fetchCallHistory();
    toast.info("Call ended");
  };

  const startCallTimer = () => {
    if (callTimerRef.current) clearInterval(callTimerRef.current);
    setCallDuration(0);
    callTimerRef.current = setInterval(() => setCallDuration(prev => prev + 1), 1000);
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = isMuted;
        setIsMuted(!isMuted);
        toast.info(isMuted ? "Unmuted" : "Muted");
      }
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !isVideoOn;
        setIsVideoOn(!isVideoOn);
      }
    }
  };

  const handleTolokyToggle = async () => {
    const callId = callIdRef.current;
    if (!callId) return;
    setTolokyToggling(true);
    try {
      if (tolokyEnabled) {
        await axios.post(`${API}/comms/toloky/meeting/${callId}/disable`, {}, {
          headers: getHeaders(), withCredentials: true,
        });
        setTolokyEnabled(false);
        toast.info("Toloky off");
      } else {
        await axios.post(`${API}/comms/toloky/meeting/${callId}/enable`, {}, {
          headers: getHeaders(), withCredentials: true,
        });
        setTolokyEnabled(true);
        toast.success("Toloky interpreter active");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to toggle Toloky");
    }
    setTolokyToggling(false);
  };

  const formatDuration = (s) => `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;
  const getInitials = (name) => name ? name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0,2) : "?";
  const getCallIcon = (call) => {
    if (call.status === "missed") return <PhoneMissed className="w-4 h-4 text-red-500" />;
    if (call.type === "incoming") return <PhoneIncoming className="w-4 h-4 text-emerald-500" />;
    return <PhoneOutgoing className="w-4 h-4 text-blue-500" />;
  };

  const showVideo = isVideoOn || activeCall?.isVideo;

  return (
    <div className="h-screen flex flex-col bg-slate-100">
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-white flex items-center justify-between">
        <div>
          <h1 className="font-['Barlow_Condensed'] text-2xl font-bold">CALLS</h1>
          <p className="text-emerald-100 text-sm">Voice & video calls</p>
        </div>
        <Button
          onClick={() => setShowCallpal(!showCallpal)}
          variant={showCallpal ? "default" : "outline"}
          className={showCallpal ? "bg-app-card text-emerald-600 hover:bg-slate-100" : "border-white text-white hover:bg-app-card/20"}
          data-testid="callpal-toggle"
        >
          <Bot className="w-4 h-4 mr-2" />
          Callpal
        </Button>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="history"><Clock className="w-4 h-4 mr-2" />Call History</TabsTrigger>
              <TabsTrigger value="dial"><Phone className="w-4 h-4 mr-2" />Make a Call</TabsTrigger>
            </TabsList>

            <TabsContent value="history">
              <div className="bg-app-card rounded-xl shadow-sm border">
                <ScrollArea className="h-[60vh]">
                  <div className="divide-y">
                    {callHistory.map(call => (
                      <div key={call.id} className="p-4 hover:bg-app flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-12 h-12">
                            <AvatarFallback className={call.status === "missed" ? "bg-red-100 text-red-600" : call.type === "incoming" ? "bg-emerald-100 text-emerald-600" : "bg-blue-100 text-blue-600"}>
                              {getInitials(call.participant?.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-app">{call.participant?.name || "Unknown"}</p>
                            <div className="flex items-center gap-2 text-sm text-app-secondary">
                              {getCallIcon(call)}
                              <span className={call.status === "missed" ? "text-red-500" : ""}>{call.status === "missed" ? "Missed" : call.type === "incoming" ? "Incoming" : "Outgoing"}</span>
                              <span>•</span>
                              <span>{call.created_at ? formatDistanceToNow(new Date(call.created_at), { addSuffix: true }) : "-"}</span>
                              {call.duration > 0 && <><span>•</span><span>{formatDuration(call.duration)}</span></>}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="ghost" onClick={() => initiateCall(call.participant, false)} className="text-emerald-600 hover:bg-emerald-50"><Phone className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => initiateCall(call.participant, true)} className="text-blue-600 hover:bg-blue-50"><Video className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    ))}
                    {callHistory.length === 0 && <div className="p-12 text-center text-app-secondary"><Phone className="w-12 h-12 mx-auto mb-3 text-app-secondary" /><p>No call history yet</p></div>}
                  </div>
                </ScrollArea>
              </div>
            </TabsContent>

            <TabsContent value="dial">
              <div className="bg-app-card rounded-xl shadow-sm border p-6">
                <div className="max-w-md mx-auto">
                  <div className="relative mb-6">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-app-secondary" />
                    <Input value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); searchUsers(e.target.value); }} placeholder="Search contacts..." className="pl-10 h-12" />
                  </div>
                  {searchResults.length > 0 && (
                    <div className="mb-6 bg-app-card border rounded-lg shadow-lg max-h-60 overflow-auto" data-testid="search-results-list">
                      {searchResults.map(user => (
                        <div key={user.user_id} className="p-3 hover:bg-app flex items-center justify-between" data-testid={`search-result-${user.user_id}`}>
                          <div className="flex items-center gap-3">
                            <Avatar className="w-10 h-10"><AvatarFallback>{getInitials(user.name)}</AvatarFallback></Avatar>
                            <div><p className="font-medium text-sm">{user.name}</p><p className="text-xs text-app-secondary">{user.email}</p></div>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => initiateCall(user, false)} className="bg-emerald-500 hover:bg-emerald-600" data-testid={`audio-call-btn-${user.user_id}`}><Phone className="w-4 h-4" /></Button>
                            <Button size="sm" onClick={() => initiateCall(user, true)} className="bg-blue-500 hover:bg-blue-600" data-testid={`video-call-btn-${user.user_id}`}><Video className="w-4 h-4" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <h3 className="text-sm font-medium text-app mb-4">Recent Contacts</h3>
                  <div className="grid grid-cols-4 gap-4">
                    {contacts.slice(0, 8).map(contact => (
                      <button key={contact.user_id} onClick={() => initiateCall(contact, false)} className="flex flex-col items-center p-3 rounded-xl hover:bg-app">
                        <Avatar className="w-12 h-12 mb-2"><AvatarFallback className="bg-emerald-100 text-emerald-600">{getInitials(contact.name)}</AvatarFallback></Avatar>
                        <span className="text-xs text-app truncate w-full text-center">{contact.name?.split(" ")[0]}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Callpal Panel */}
        {showCallpal && (
          <div className="w-96 border-l border-app bg-app-card">
            <AIPalChat
              palId="callpal"
              palName="Callpal"
              palIcon={Phone}
              color="from-emerald-500 to-teal-500"
              onClose={() => setShowCallpal(false)}
            />
          </div>
        )}
      </div>

      {/* Incoming Call */}
      <Dialog open={!!incomingCall} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md [&>button]:hidden" data-testid="incoming-call-dialog">
          <div className="text-center py-6">
            <Avatar className="w-24 h-24 mx-auto mb-4 animate-pulse">
              <AvatarFallback className="text-2xl bg-emerald-100 text-emerald-600">{getInitials(incomingCall?.caller?.name)}</AvatarFallback>
            </Avatar>
            <h3 className="text-xl font-semibold text-app" data-testid="incoming-call-caller-name">{incomingCall?.caller?.name || "Unknown"}</h3>
            <p className="text-app-secondary mb-6">Incoming {incomingCall?.call_type === "video" ? "video" : "voice"} call...</p>
            <div className="flex justify-center gap-6">
              <Button onClick={declineCall} className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600" data-testid="decline-call-btn"><PhoneOff className="w-6 h-6" /></Button>
              <Button onClick={answerCall} className="w-16 h-16 rounded-full bg-emerald-500 hover:bg-emerald-600 animate-pulse" data-testid="answer-call-btn"><Phone className="w-6 h-6" /></Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Active Call */}
      <Dialog open={!!activeCall} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-2xl [&>button]:hidden" data-testid="active-call-dialog">
          <div className="py-4">
            <div className="relative mb-4 bg-slate-900 rounded-lg overflow-hidden" style={{ height: showVideo ? 400 : 200 }}>
              {/* Dedicated audio element — small non-zero size so browser decodes the audio */}
              <audio ref={remoteAudioRef} autoPlay playsInline className="absolute" style={{ left: '-9999px', width: '10px', height: '10px' }} />

              {/* Remote video element */}
              <video
                ref={remoteVideoRef}
                className={`w-full h-full object-cover bg-slate-800 ${showVideo ? '' : 'invisible absolute w-0 h-0'}`}
                data-testid="remote-video"
                autoPlay
                playsInline
              />
              {showVideo ? (
                <>
                  <div className="absolute bottom-4 right-4 w-32 h-24 bg-slate-800 rounded-lg overflow-hidden border-2 border-white shadow-lg">
                    <video ref={localVideoRef} className="w-full h-full object-cover" autoPlay playsInline muted />
                  </div>
                </>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Avatar className="w-32 h-32"><AvatarFallback className="text-4xl bg-slate-700 text-white">{getInitials(activeCall?.participant?.name)}</AvatarFallback></Avatar>
                </div>
              )}
              {(callStatus === "ringing" || callStatus === "connecting") && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                  <div className="text-white text-center">
                    <Loader2 className="w-12 h-12 animate-spin mx-auto mb-3" />
                    <p className="text-lg">{callStatus === "ringing" ? "Calling..." : "Connecting..."}</p>
                    {connectionState && <p className="text-sm text-app-secondary mt-1">ICE: {connectionState}</p>}
                  </div>
                </div>
              )}
            </div>
            <div className="text-center mb-4">
              <h3 className="text-xl font-semibold text-app">{activeCall?.participant?.name}</h3>
              <p className="text-app-secondary">{callStatus === "connected" ? formatDuration(callDuration) : callStatus === "ringing" ? "Ringing..." : "Connecting..."}</p>
            </div>
            <div className="flex justify-center gap-4 items-center">
              <Button variant="outline" onClick={toggleMute} className={`w-14 h-14 rounded-full ${isMuted ? "bg-red-100 text-red-600" : ""}`} data-testid="mute-btn">{isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}</Button>
              <Button variant="outline" onClick={toggleVideo} className={`w-14 h-14 rounded-full ${isVideoOn ? "bg-blue-100 text-blue-600" : ""}`} data-testid="toggle-video-btn">{isVideoOn ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}</Button>

              {callIdRef.current && (
                <Button
                  onClick={handleTolokyToggle}
                  disabled={tolokyToggling}
                  variant={tolokyEnabled ? "default" : "outline"}
                  size="sm"
                  className={`w-14 h-14 rounded-full gap-1 ${tolokyEnabled ? "bg-violet-600 hover:bg-violet-700 text-white" : ""}`}
                >
                  {tolokyToggling ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Languages className="w-5 h-5" />
                  )}
                </Button>
              )}

              <Button onClick={handleEndCall} className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600" data-testid="end-call-btn"><PhoneOff className="w-6 h-6" /></Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
